/**
 * M2M API clients tab - shown on the Partner detail page under "Integrations".
 *
 * Lets partner_admin self-service create, view, rotate, and revoke their
 * M2M API credentials.  Super-admin sees all clients + can grant elevated
 * scopes / rate tiers.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronRight,
  ClipboardCopy,
  ExternalLink,
  FlaskConical,
  Key,
  KeyRound,
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  Trash2,
} from "lucide-react";
import { useRef, useState } from "react";

import { ModalOverlay } from "@/components/ModalOverlay";
import { apiClient } from "@/lib/api";
import { describe } from "@/lib/api-error";
import { isSuperAdmin } from "@/lib/roles";
import { useRoleProfile } from "@/lib/use-role-profile";
import { cn } from "@/lib/utils";
import type {
  M2MClient,
  M2MClientCreatedResponse,
  M2MClientUpdateRequest,
  M2MScope,
  M2MRateLimitTier,
  M2MTestScenario,
  M2MTestStep,
  Uuid,
} from "@tilt/admin-sdk";

// ------------------------------------------------------------------ //
// Constants                                                           //
// ------------------------------------------------------------------ //

const ALL_SCOPES: { scope: M2MScope; label: string; description: string }[] = [
  {
    scope: "payments:read",
    label: "payments:read",
    description: "Read orders, payments, and transactions",
  },
  {
    scope: "payments:create",
    label: "payments:create",
    description: "Create orders and submit payments",
  },
  {
    scope: "payments:refund",
    label: "payments:refund",
    description: "Refund, void, and cancel payments",
  },
  {
    scope: "locations:read",
    label: "locations:read",
    description: "List locations visible to this partner",
  },
  {
    scope: "locations:write",
    label: "locations:write",
    description: "Manage location settings",
  },
  {
    scope: "webhooks:subscribe",
    label: "webhooks:subscribe",
    description: "Register webhook endpoints to receive payment events (super_admin only)",
  },
  {
    scope: "pos:launch",
    label: "pos:launch",
    description: "Mint partner SSO launch tokens to deep-link external users into the POS",
  },
];

const RATE_TIERS: { value: M2MRateLimitTier; label: string }[] = [
  { value: "low", label: "Low (10 req/s)" },
  { value: "standard", label: "Standard (100 req/s)" },
  { value: "high", label: "High (500 req/s)" },
  { value: "enterprise", label: "Enterprise (2000 req/s)" },
];

// ------------------------------------------------------------------ //
// Main tab                                                            //
// ------------------------------------------------------------------ //

export function M2MClientsTab(props: {
  partnerId: Uuid;
  partnerPath: string;
  canWrite: boolean;
}): JSX.Element {
  const profile = useRoleProfile();
  const superAdmin = isSuperAdmin(profile);
  const queryClient = useQueryClient();

  const [createOpen, setCreateOpen] = useState(false);
  const [createdSecret, setCreatedSecret] = useState<M2MClientCreatedResponse | null>(null);
  const [editTarget, setEditTarget] = useState<M2MClient | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<M2MClient | null>(null);
  const [rotateTarget, setRotateTarget] = useState<M2MClient | null>(null);
  const [testClientId, setTestClientId] = useState<Uuid | null>(null);

  const clients = useQuery({
    queryKey: ["m2m-clients", props.partnerId],
    queryFn: () => apiClient.listM2MClients(props.partnerId),
    retry: false,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["m2m-clients", props.partnerId] });
  };

  // Derive env from the API base URL: pos-api.{env}.apps → m2m pool name
  const apiBase = import.meta.env.VITE_API_BASE_URL ?? "";
  const envMatch = apiBase.match(/pos-api\.([^.]+)\.apps/);
  const m2mEnv = envMatch ? envMatch[1] : "dev";
  const isProd = m2mEnv === "prod";
  // Raw Cognito URL - branded auth.{env}.apps.myfinterra.com pending
  // Cognito custom domain limit increase (V1 pools consume all 4 slots).
  const cognitoRegion = "us-east-1";
  const tokenUrl = isProd
    ? `https://tilt-m2m-prod.auth.${cognitoRegion}.amazoncognito.com/oauth2/token`
    : `https://tilt-m2m-${m2mEnv}.auth.${cognitoRegion}.amazoncognito.com/oauth2/token`;
  const docsUrl = isProd
    ? "https://docs.apps.myfinterra.com"
    : `https://docs.${m2mEnv}.apps.myfinterra.com`;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold">API Clients</h2>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Machine-to-machine credentials for payment API access.{" "}
            <a
              href={docsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-0.5 text-primary hover:underline"
            >
              Developer docs <ExternalLink className="h-3 w-3" />
            </a>
          </p>
        </div>
        {props.canWrite && (
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90"
          >
            <Plus className="h-3.5 w-3.5" /> New client
          </button>
        )}
      </div>

      {/* Token URL reference */}
      <div className="rounded-md border bg-muted/40 p-3 text-xs">
        <span className="font-medium text-foreground">Token URL: </span>
        <code className="font-mono text-muted-foreground">{tokenUrl}</code>
        <p className="mt-1 text-muted-foreground">
          Exchange <code className="font-mono">client_id</code> +{" "}
          <code className="font-mono">client_secret</code> with{" "}
          <code className="font-mono">grant_type=client_credentials</code> to obtain an access
          token.
        </p>
      </div>

      {clients.isError && (
        <div className="rounded-md bg-destructive/10 p-3 text-xs text-destructive">
          {describe(clients.error)}
        </div>
      )}

      {clients.isLoading && <p className="text-xs text-muted-foreground">Loading clients…</p>}

      {clients.data && clients.data.items.length === 0 && (
        <div className="rounded-md border border-dashed p-6 text-center">
          <KeyRound className="mx-auto mb-2 h-6 w-6 text-muted-foreground" />
          <p className="text-sm font-medium">No API clients yet</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Create a client to generate machine-to-machine credentials.
          </p>
        </div>
      )}

      {clients.data && clients.data.items.length > 0 && (
        <div className="space-y-2">
          {clients.data.items.map((c) => (
            <div key={c.id}>
              <ClientRow
                client={c}
                canWrite={props.canWrite}
                isSuperAdmin={superAdmin}
                testOpen={testClientId === c.id}
                onEdit={() => setEditTarget(c)}
                onRevoke={() => setRevokeTarget(c)}
                onRotate={() => setRotateTarget(c)}
                onToggleTest={() => setTestClientId((prev) => (prev === c.id ? null : c.id))}
              />
              {testClientId === c.id && <M2MTestPanel partnerId={props.partnerId} client={c} />}
            </div>
          ))}
        </div>
      )}

      {createOpen && (
        <CreateDialog
          partnerId={props.partnerId}
          isSuperAdmin={superAdmin}
          onClose={() => setCreateOpen(false)}
          onCreated={(resp) => {
            invalidate();
            setCreateOpen(false);
            setCreatedSecret(resp);
          }}
        />
      )}

      {editTarget && (
        <EditDialog
          partnerId={props.partnerId}
          client={editTarget}
          isSuperAdmin={superAdmin}
          onClose={() => setEditTarget(null)}
          onSaved={() => {
            invalidate();
            setEditTarget(null);
          }}
        />
      )}

      {createdSecret && (
        <SecretRevealDialog created={createdSecret} onClose={() => setCreatedSecret(null)} />
      )}

      {revokeTarget && (
        <RevokeDialog
          partnerId={props.partnerId}
          client={revokeTarget}
          onClose={() => setRevokeTarget(null)}
          onRevoked={() => {
            invalidate();
            setRevokeTarget(null);
          }}
        />
      )}

      {rotateTarget && (
        <RotateDialog
          partnerId={props.partnerId}
          client={rotateTarget}
          onClose={() => setRotateTarget(null)}
          onRotated={(resp) => {
            invalidate();
            setRotateTarget(null);
            setCreatedSecret(resp);
          }}
        />
      )}
    </div>
  );
}

// ------------------------------------------------------------------ //
// Client row                                                          //
// ------------------------------------------------------------------ //

function ClientRow(props: {
  client: M2MClient;
  canWrite: boolean;
  isSuperAdmin: boolean;
  testOpen: boolean;
  onEdit: () => void;
  onRevoke: () => void;
  onRotate: () => void;
  onToggleTest: () => void;
}): JSX.Element {
  const c = props.client;
  const inGrace = c.rotate_grace_until !== null && new Date(c.rotate_grace_until) > new Date();

  return (
    <div
      className={cn("rounded-md border bg-card p-3", props.testOpen && "rounded-b-none border-b-0")}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Key className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <span className="truncate text-sm font-medium">{c.name}</span>
            <span className="rounded-full border px-1.5 py-0.5 text-[10px] font-medium">
              {c.rate_limit_tier}
            </span>
            {inGrace && (
              <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                rotation grace
              </span>
            )}
          </div>
          {c.description && (
            <p className="mt-0.5 truncate text-xs text-muted-foreground">{c.description}</p>
          )}
          <div className="mt-1.5 flex flex-wrap gap-1">
            {c.scopes.map((s) => (
              <span
                key={s}
                className="rounded border bg-muted/60 px-1.5 py-0.5 font-mono text-[10px]"
              >
                {s}
              </span>
            ))}
          </div>
          <p className="mt-1 text-[10px] text-muted-foreground">
            <span className="font-mono">{c.client_id}</span> · created{" "}
            {new Date(c.created_at).toLocaleDateString()}
          </p>
        </div>
        <div className="flex shrink-0 gap-1">
          <button
            type="button"
            onClick={props.onToggleTest}
            title="Test this client"
            aria-label="Test this client"
            className={cn(
              "flex items-center gap-1 rounded-md border px-2 py-1.5 text-[10px] font-medium",
              props.testOpen
                ? "border-primary bg-primary/10 text-primary"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            <FlaskConical className="h-3.5 w-3.5" />
            Test
          </button>
          {props.canWrite && (
            <>
              <ActionButton
                icon={Pencil}
                label="Edit scopes"
                onClick={props.onEdit}
                variant="neutral"
              />
              <ActionButton
                icon={RefreshCw}
                label="Rotate secret"
                onClick={props.onRotate}
                variant="neutral"
              />
              <ActionButton
                icon={Trash2}
                label="Revoke client"
                onClick={props.onRevoke}
                variant="destructive"
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ActionButton(props: {
  icon: typeof Trash2;
  label: string;
  onClick: () => void;
  variant: "neutral" | "destructive";
}): JSX.Element {
  const Icon = props.icon;
  return (
    <button
      type="button"
      onClick={props.onClick}
      title={props.label}
      aria-label={props.label}
      className={cn(
        "rounded-md border p-1.5 text-muted-foreground hover:text-foreground",
        props.variant === "destructive" && "hover:border-destructive hover:text-destructive",
      )}
    >
      <Icon className="h-3.5 w-3.5" />
    </button>
  );
}

// ------------------------------------------------------------------ //
// M2M Developer Test Panel                                           //
// ------------------------------------------------------------------ //

const SCENARIOS: {
  value: M2MTestScenario;
  label: string;
  description: string;
  needsPhone?: boolean;
}[] = [
  {
    value: "token_exchange",
    label: "Token exchange",
    description: "Verify credentials → decode claims",
  },
  {
    value: "list_orders",
    label: "List orders",
    description: "Token + GET /pos/v1/orders (tests payments:read)",
  },
  {
    value: "list_locations",
    label: "List locations",
    description: "Token + GET /pos/v1/locations (tests locations:read)",
  },
  {
    value: "create_order",
    label: "Create test order",
    description: "Token + POST /pos/v1/orders with $1.00 test payload",
  },
  {
    value: "pay_by_text",
    label: "Pay-by-text",
    description: "Create order → initiate card/pay_by_text - customer gets SMS link",
    needsPhone: true,
  },
  {
    value: "ach",
    label: "ACH (Aeropay)",
    description: "Create order → initiate ACH - customer gets bank-link SMS",
    needsPhone: true,
  },
  {
    value: "void",
    label: "Void order",
    description: "Create $1 order → void it (tests payments:refund scope)",
  },
  {
    value: "refund",
    label: "Refund",
    description: "Create $1 cash order → full refund (tests payments:refund scope)",
  },
  {
    value: "hosted_checkout",
    label: "Hosted checkout",
    description: "Create order with return_url → verify hosted_checkout_url returned",
  },
  {
    value: "launch_pos",
    label: "Launch POS (SSO)",
    description:
      "Mint a launch token → get URL that opens /sale as a location_cashier (requires pos:launch scope)",
  },
];

function M2MTestPanel(props: { partnerId: Uuid; client: M2MClient }): JSX.Element {
  const [secret, setSecret] = useState("");
  const [scenario, setScenario] = useState<M2MTestScenario>("token_exchange");
  const [phone, setPhone] = useState("");
  const [steps, setSteps] = useState<M2MTestStep[] | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [expandedStep, setExpandedStep] = useState<number | null>(null);
  const [runError, setRunError] = useState<string | null>(null);

  const needsPhone = SCENARIOS.find((s) => s.value === scenario)?.needsPhone ?? false;

  const run = useMutation({
    mutationFn: () =>
      apiClient.testM2MClient(props.partnerId, props.client.id, {
        client_secret: secret,
        scenario,
        ...(needsPhone && phone ? { customer_phone: phone } : {}),
      }),
    onSuccess: (data) => {
      setSteps(data.steps);
      setWarning(data.warning ?? null);
      setRunError(null);
      setExpandedStep(data.steps.length - 1);
    },
    onError: (err) => {
      setRunError(describe(err));
      setSteps(null);
    },
  });

  return (
    <div className="rounded-b-md border border-t-0 bg-muted/20 p-4 space-y-4">
      <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
        <FlaskConical className="h-3.5 w-3.5" />
        Developer Console - {props.client.name}
      </div>

      {/* Secret input */}
      <div className="space-y-1">
        <label htmlFor={`test-secret-${props.client.id}`} className="block text-xs font-medium">
          Client secret
        </label>
        <input
          id={`test-secret-${props.client.id}`}
          type="password"
          value={secret}
          onChange={(e) => setSecret(e.target.value)}
          placeholder="Paste the secret you stored at creation…"
          className="w-full rounded-md border bg-background px-2.5 py-1.5 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-primary"
          autoComplete="off"
        />
      </div>

      {/* Phone input - only for async scenarios */}
      {needsPhone && (
        <div className="space-y-1">
          <label htmlFor={`test-phone-${props.client.id}`} className="block text-xs font-medium">
            Customer phone (receives SMS)
          </label>
          <input
            id={`test-phone-${props.client.id}`}
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="e.g. 541-602-0935"
            className="w-full rounded-md border bg-background px-2.5 py-1.5 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-primary"
            autoComplete="tel"
          />
        </div>
      )}

      {/* Scenario picker */}
      <div className="space-y-1">
        <span className="block text-xs font-medium">Scenario</span>
        <div className="flex flex-wrap gap-2">
          {SCENARIOS.map((s) => (
            <button
              key={s.value}
              type="button"
              onClick={() => setScenario(s.value)}
              className={cn(
                "rounded-md border px-2.5 py-1.5 text-left text-xs",
                scenario === s.value
                  ? "border-primary bg-primary/10 text-primary"
                  : "hover:bg-muted/50",
              )}
            >
              <span className="font-medium">{s.label}</span>
              <span className="ml-1.5 text-muted-foreground">{s.description}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Run button */}
      <button
        type="button"
        disabled={!secret || (needsPhone && !phone) || run.isPending}
        onClick={() => run.mutate()}
        className="flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
      >
        {run.isPending ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <FlaskConical className="h-3.5 w-3.5" />
        )}
        {run.isPending ? "Running…" : "Run test"}
      </button>

      {/* Error */}
      {runError && (
        <div className="flex items-center gap-2 rounded-md bg-destructive/10 p-2.5 text-xs text-destructive">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          {runError}
        </div>
      )}

      {/* Warning */}
      {warning && (
        <div className="flex items-center gap-2 rounded-md bg-amber-50 p-2.5 text-xs text-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          {warning}
        </div>
      )}

      {/* Step results */}
      {steps && steps.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium">Results</p>
          {steps.map((step, i) => (
            <div key={i} className="rounded-md border bg-card text-xs overflow-hidden">
              <button
                type="button"
                onClick={() => setExpandedStep((prev) => (prev === i ? null : i))}
                className="flex w-full items-center justify-between px-3 py-2 hover:bg-muted/30"
              >
                <div className="flex items-center gap-2">
                  {expandedStep === i ? (
                    <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                  )}
                  <span className="font-mono font-medium">{step.name}</span>
                </div>
                <span
                  className={cn(
                    "rounded-full px-2 py-0.5 text-[10px] font-medium",
                    step.ok
                      ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
                      : "bg-destructive/10 text-destructive",
                  )}
                >
                  {step.status === 0 ? "error" : step.status} {step.ok ? "OK" : "FAIL"}
                </span>
              </button>
              {expandedStep === i && (
                <div className="border-t bg-muted/20 p-3 space-y-2">
                  {step.request && (
                    <div>
                      <p className="mb-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                        Request
                      </p>
                      <pre className="overflow-auto rounded bg-background p-2 text-[10px] leading-relaxed">
                        {JSON.stringify(step.request, null, 2)}
                      </pre>
                    </div>
                  )}
                  <div>
                    <p className="mb-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                      Response
                    </p>
                    <pre className="overflow-auto rounded bg-background p-2 text-[10px] leading-relaxed">
                      {JSON.stringify(step.body, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ //
// Create dialog                                                       //
// ------------------------------------------------------------------ //

function CreateDialog(props: {
  partnerId: Uuid;
  isSuperAdmin: boolean;
  onClose: () => void;
  onCreated: (resp: M2MClientCreatedResponse) => void;
}): JSX.Element {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedScopes, setSelectedScopes] = useState<Set<M2MScope>>(
    new Set(["payments:read", "payments:create"]),
  );
  const [rateTier, setRateTier] = useState<M2MRateLimitTier>("standard");
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => {
      const body: Parameters<typeof apiClient.createM2MClient>[1] = {
        name: name.trim(),
        scopes: Array.from(selectedScopes),
        rate_limit_tier: rateTier,
      };
      if (description.trim()) body.description = description.trim();
      return apiClient.createM2MClient(props.partnerId, body);
    },
    onSuccess: (resp) => props.onCreated(resp),
    onError: (err) => setError(describe(err)),
  });

  const toggleScope = (scope: M2MScope) => {
    setSelectedScopes((prev) => {
      const next = new Set(prev);
      if (next.has(scope)) next.delete(scope);
      else next.add(scope);
      return next;
    });
  };

  return (
    <DialogShell title="New API client" onClose={props.onClose} onSubmit={() => create.mutate()}>
      <Field label="Name">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Acme DMS Integration"
          maxLength={100}
          required
          className="w-full rounded-md border bg-background px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </Field>

      <Field label="Description (optional)">
        <input
          type="text"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Short note about this client's purpose"
          maxLength={500}
          className="w-full rounded-md border bg-background px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </Field>

      <div>
        <span className="mb-2 block text-xs font-medium text-muted-foreground">Scopes</span>
        <div className="space-y-1.5">
          {ALL_SCOPES.map(({ scope, label, description: desc }) => {
            const superOnly = scope === "webhooks:subscribe";
            const disabled = superOnly && !props.isSuperAdmin;
            return (
              <label
                key={scope}
                className={cn(
                  "flex cursor-pointer items-start gap-2 rounded-md border p-2 text-xs",
                  selectedScopes.has(scope) ? "border-primary bg-primary/5" : "hover:bg-muted/30",
                  disabled && "cursor-not-allowed opacity-40",
                )}
              >
                <input
                  type="checkbox"
                  checked={selectedScopes.has(scope)}
                  onChange={() => !disabled && toggleScope(scope)}
                  disabled={disabled}
                  className="mt-0.5 shrink-0"
                />
                <div>
                  <span className="font-mono font-medium">{label}</span>
                  {superOnly && (
                    <span className="ml-1.5 rounded-full bg-amber-100 px-1 py-0.5 text-[9px] font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                      super_admin
                    </span>
                  )}
                  <p className="mt-0.5 text-muted-foreground">{desc}</p>
                </div>
              </label>
            );
          })}
        </div>
      </div>

      {props.isSuperAdmin && (
        <Field label="Rate limit tier">
          <select
            value={rateTier}
            onChange={(e) => setRateTier(e.target.value as M2MRateLimitTier)}
            className="w-full rounded-md border bg-background px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {RATE_TIERS.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </Field>
      )}

      {error && (
        <p className="flex items-center gap-1.5 text-xs text-destructive">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          {error}
        </p>
      )}

      <DialogActions
        busy={create.isPending}
        disabled={!name.trim() || selectedScopes.size === 0}
        submitLabel="Create client"
        onCancel={props.onClose}
      />
    </DialogShell>
  );
}

// ------------------------------------------------------------------ //
// Edit scopes dialog                                                  //
// ------------------------------------------------------------------ //

function EditDialog(props: {
  partnerId: Uuid;
  client: M2MClient;
  isSuperAdmin: boolean;
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const c = props.client;
  const [selectedScopes, setSelectedScopes] = useState<Set<M2MScope>>(new Set(c.scopes));
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const body: M2MClientUpdateRequest = { scopes: Array.from(selectedScopes) };
      return apiClient.updateM2MClient(props.partnerId, c.id, body);
    },
    onSuccess: () => props.onSaved(),
    onError: (err) => setError(describe(err)),
  });

  const toggleScope = (scope: M2MScope) => {
    setSelectedScopes((prev) => {
      const next = new Set(prev);
      if (next.has(scope)) next.delete(scope);
      else next.add(scope);
      return next;
    });
  };

  return (
    <DialogShell
      title={`Edit scopes - ${c.name}`}
      onClose={props.onClose}
      onSubmit={() => save.mutate()}
    >
      <p className="text-xs text-muted-foreground">
        Changing scopes takes effect on the <em>next</em> token exchange. Active tokens remain valid
        until they expire.
      </p>

      <div>
        <span className="mb-2 block text-xs font-medium text-muted-foreground">Scopes</span>
        <div className="space-y-1.5">
          {ALL_SCOPES.map(({ scope, label, description: desc }) => {
            const superOnly = scope === "webhooks:subscribe";
            const disabled = superOnly && !props.isSuperAdmin;
            return (
              <label
                key={scope}
                className={cn(
                  "flex cursor-pointer items-start gap-2 rounded-md border p-2 text-xs",
                  selectedScopes.has(scope) ? "border-primary bg-primary/5" : "hover:bg-muted/30",
                  disabled && "cursor-not-allowed opacity-40",
                )}
              >
                <input
                  type="checkbox"
                  checked={selectedScopes.has(scope)}
                  onChange={() => !disabled && toggleScope(scope)}
                  disabled={disabled}
                  className="mt-0.5 shrink-0"
                />
                <div>
                  <span className="font-mono font-medium">{label}</span>
                  {superOnly && (
                    <span className="ml-1.5 rounded-full bg-amber-100 px-1 py-0.5 text-[9px] font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                      super_admin
                    </span>
                  )}
                  <p className="mt-0.5 text-muted-foreground">{desc}</p>
                </div>
              </label>
            );
          })}
        </div>
      </div>

      {error && (
        <p className="flex items-center gap-1.5 text-xs text-destructive">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          {error}
        </p>
      )}

      <DialogActions
        busy={save.isPending}
        disabled={selectedScopes.size === 0}
        submitLabel="Save scopes"
        onCancel={props.onClose}
      />
    </DialogShell>
  );
}

// ------------------------------------------------------------------ //
// Secret reveal dialog (shown once after create / rotate)            //
// ------------------------------------------------------------------ //

function SecretRevealDialog(props: {
  created: M2MClientCreatedResponse;
  onClose: () => void;
}): JSX.Element {
  const [copied, setCopied] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const secretRef = useRef<HTMLTextAreaElement>(null);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(props.created.client_secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      secretRef.current?.select();
    }
  };

  return (
    <ModalOverlay ariaLabel="New API client secret" onClose={() => confirmed && props.onClose()}>
      <div className="w-full max-w-md space-y-4 rounded-t-xl border bg-card p-5 shadow-lg sm:rounded-xl">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
            <Key className="h-4.5 w-4.5 text-green-600 dark:text-green-400" />
          </div>
          <div>
            <h2 className="text-sm font-semibold">API client created: {props.created.name}</h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Copy the secret now - it will not be shown again.
            </p>
          </div>
        </div>

        <div className="space-y-1.5">
          <span className="block text-xs font-medium text-muted-foreground">Client ID</span>
          <div className="flex items-center gap-2">
            <code className="flex-1 truncate rounded-md border bg-muted px-2.5 py-1.5 font-mono text-xs">
              {props.created.client_id}
            </code>
          </div>
        </div>

        <div className="space-y-1.5">
          <label
            htmlFor="secret-reveal-textarea"
            className="block text-xs font-medium text-muted-foreground"
          >
            Client secret
          </label>
          <div className="relative">
            <textarea
              id="secret-reveal-textarea"
              ref={secretRef}
              readOnly
              rows={2}
              value={props.created.client_secret}
              className="w-full resize-none rounded-md border bg-muted px-2.5 py-1.5 font-mono text-xs focus:outline-none"
            />
            <button
              type="button"
              onClick={() => void copy()}
              aria-label={copied ? "Copied" : "Copy secret"}
              className="absolute right-2 top-2 rounded-md bg-background p-1 text-muted-foreground shadow-sm hover:text-foreground"
            >
              {copied ? (
                <Check className="h-3.5 w-3.5 text-green-600" />
              ) : (
                <ClipboardCopy className="h-3.5 w-3.5" />
              )}
            </button>
          </div>
        </div>

        <div className="rounded-md bg-amber-50 p-3 text-xs text-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
          <AlertTriangle className="mb-1 h-3.5 w-3.5" />
          {props.created._secret_notice}
        </div>

        <label className="flex cursor-pointer items-start gap-2 text-xs">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            className="mt-0.5 shrink-0"
          />
          I have stored the secret in a secure location and understand it cannot be recovered.
        </label>

        <button
          type="button"
          disabled={!confirmed}
          onClick={props.onClose}
          className="w-full rounded-md bg-primary px-3 py-2 text-xs font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
        >
          Done
        </button>
      </div>
    </ModalOverlay>
  );
}

// ------------------------------------------------------------------ //
// Revoke dialog                                                       //
// ------------------------------------------------------------------ //

function RevokeDialog(props: {
  partnerId: Uuid;
  client: M2MClient;
  onClose: () => void;
  onRevoked: () => void;
}): JSX.Element {
  const [error, setError] = useState<string | null>(null);

  const revoke = useMutation({
    mutationFn: () => apiClient.revokeM2MClient(props.partnerId, props.client.id),
    onSuccess: props.onRevoked,
    onError: (err) => setError(describe(err)),
  });

  return (
    <ModalOverlay ariaLabel="Revoke API client" onClose={props.onClose}>
      <div className="w-full max-w-sm space-y-4 rounded-t-xl border bg-card p-5 shadow-lg sm:rounded-xl">
        <h2 className="text-sm font-semibold">Revoke "{props.client.name}"?</h2>
        <p className="text-xs text-muted-foreground">
          Any active tokens issued to this client will stop working immediately. This cannot be
          undone.
        </p>
        {error && (
          <p className="flex items-center gap-1.5 text-xs text-destructive">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={props.onClose}
            className="rounded-md border px-3 py-1.5 text-xs"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={revoke.isPending}
            onClick={() => revoke.mutate()}
            className="rounded-md bg-destructive px-3 py-1.5 text-xs font-medium text-destructive-foreground hover:opacity-90 disabled:opacity-50"
          >
            Revoke client
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}

// ------------------------------------------------------------------ //
// Rotate dialog                                                       //
// ------------------------------------------------------------------ //

function RotateDialog(props: {
  partnerId: Uuid;
  client: M2MClient;
  onClose: () => void;
  onRotated: (resp: M2MClientCreatedResponse) => void;
}): JSX.Element {
  const [error, setError] = useState<string | null>(null);

  const rotate = useMutation({
    mutationFn: () => apiClient.rotateM2MClient(props.partnerId, props.client.id),
    onSuccess: props.onRotated,
    onError: (err) => setError(describe(err)),
  });

  return (
    <ModalOverlay ariaLabel="Rotate API client secret" onClose={props.onClose}>
      <div className="w-full max-w-sm space-y-4 rounded-t-xl border bg-card p-5 shadow-lg sm:rounded-xl">
        <h2 className="text-sm font-semibold">Rotate secret for "{props.client.name}"</h2>
        <p className="text-xs text-muted-foreground">
          A new secret will be generated. The old secret remains valid for 24 hours to allow
          zero-downtime rotation. After that grace window it will be rejected.
        </p>
        {error && (
          <p className="flex items-center gap-1.5 text-xs text-destructive">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={props.onClose}
            className="rounded-md border px-3 py-1.5 text-xs"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={rotate.isPending}
            onClick={() => rotate.mutate()}
            className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            Rotate secret
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}

// ------------------------------------------------------------------ //
// Shared primitives                                                   //
// ------------------------------------------------------------------ //

function DialogShell(props: {
  title: string;
  onClose: () => void;
  onSubmit: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <ModalOverlay ariaLabel={props.title} onClose={props.onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          props.onSubmit();
        }}
        className="max-h-[90vh] w-full max-w-md space-y-3 overflow-y-auto rounded-t-xl border bg-card p-4 shadow-lg sm:rounded-xl sm:p-5"
      >
        <h2 className="text-sm font-semibold">{props.title}</h2>
        {props.children}
      </form>
    </ModalOverlay>
  );
}

function Field(props: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-muted-foreground">{props.label}</span>
      {props.children}
    </label>
  );
}

function DialogActions(props: {
  busy: boolean;
  disabled: boolean;
  submitLabel: string;
  onCancel: () => void;
}): JSX.Element {
  return (
    <div className="flex justify-end gap-2 pt-2">
      <button
        type="button"
        onClick={props.onCancel}
        className="rounded-md border px-3 py-1.5 text-xs"
      >
        Cancel
      </button>
      <button
        type="submit"
        disabled={props.busy || props.disabled}
        className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
      >
        {props.submitLabel}
      </button>
    </div>
  );
}
